import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import Toolbar from '@material-ui/core/Toolbar';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import TariffHelper, { ILPULine, ILPUTariff } from '../../../../../../@types/model/transformer/tariffs/tariffs';
import TransformerHelper, { ITransformer } from '../../../../../../@types/model/transformer/transformer';
import { ITransformerGroup } from '../../../../../../@types/model/transformer/transformerGroup';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../../../../@types/model/transformer/invoice/transformerInvoice';
import { FirebaseService } from '../../../../../../services/firebaseService';
import generalFunctions from '../../../../../../store/general/functions';
import DropdownButton from '../../../../../customComponents/button/DropdownButton';
import TransformerGroupDropdown from '../../../../../customComponents/selector/transformer/GroupSelector';
import TariffConfirmTable from '../../../table/ConfirmTable';
import LPUCapturingTable from '../../../table/LPUCaptureTable';
import lodash from 'lodash';
import { IAuthState, IRootState } from '../../../../../../@types/redux';
import { connect } from 'react-redux';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { firestore } from 'firebase/app';
import uuid from 'uuid';
import { IValueAddedTax } from '../../../../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import MasterDataValueAddedTaxFunctions from '../../../../../../store/masterData/valueAddedTax/functions';
import { ITransformerInvoiceUpsert } from '../../../../../../@types/model/transformer/upsert/transformerInvoiceUpsert';
import { ITransformerUpsert } from '../../../../../../@types/model/transformer/upsert/transformerUpsert';
import { ILPULineUpsert } from '../../../../../../@types/model/transformer/upsert/lpuLineUpsert';
import TransformerInvoiceHttpService from '../../../../../../services/electricity/transformerInvoiceHttpService';
import Screen from '../../../../../customComponentV2/screen/Screen';

interface ITransformerLPUCaptureProps {
    auth : IAuthState;
    valueAddedTaxes : Array<IValueAddedTax>;
    onDismiss : () => void;
}

const maxDate = moment.utc().startOf('month');

const TransformerLPUCapture = (props : ITransformerLPUCaptureProps) : React.ReactElement => {
    const { auth, valueAddedTaxes } = props;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedAccountMonth, setSelectedAccountMonth] = useState<moment.Moment | null>(null);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [lpuTariffs, setLpuTariffs] = useState<Array<ILPUTariff>>([]);
    const [transformers, setTransformers] = useState<Array<ITransformer>>([]);
    const [invoices, setInvoices] = useState<Array<ITransformerInvoice<ILPULine>>>([]);
    const [currentInvoices, setCurrentInvoices] = useState<Array<ITransformerInvoice<ILPULine>>>([]);
    const [isTariffChange, setIsTariffChange] = useState<boolean>(false);
    const [isTariffConfirmationDialogOpen, setIsTariffConfirmationDialogOpen] = useState<boolean>(false);
 
    // Only set when a transformer invoice is edited, not when a new one is created
    const [selectedEpNumber, setSelectedEpNumber] = useState<string | null>(null);
    
    const loadVatMasterData = () => {
        MasterDataValueAddedTaxFunctions.load();
    };

    useEffect(() => {
        loadVatMasterData();
    }, []);

    const invoiceFromLines = (transformer : ITransformer, lines : Array<ILPULine>) : ITransformerInvoice<ILPULine> => {
        if (!selectedAccountMonth) throw new Error('Selecting Month Required');
        if (!auth.session) throw new Error('Auth Required');

        const vatPercentage = FirebaseService.getVatByMonth(selectedAccountMonth, valueAddedTaxes);

        return TransformerInvoiceHelper.calculateLPUTotal({
            guid: uuid.v4(),
            transformer,
            accountNumber: transformer.AccountNumber ?? '',
            days: lodash.sumBy(lines, line => line.days),
            epNumber: transformer.EPNumber,
            epNumberRef: TransformerHelper.doc(transformer.EPNumber).path,
            poleNumber: transformer.PoleNumber ?? '',
            lines: [...lines],
            totalUsage: 0,
            totalInterest: 0,
            reactiveEnergy: 0,
            utilizedCapacity: 0,
            vatPercentage: vatPercentage,
            totalCharge: 0,
            vatCharge: 0,
            totalDue: 0,
            totalBilled: 0,
            type: 'lpu',
            paymentDueDate: null,
            demandReading: 0,
            adminCharge: 0,
            serviceCharge: 0,
            standardConnectionCharge: 0,
            createdBy: auth.session.firebaseUser.uid,
            createdByEmployee: auth.session.employee.EmployeeNumber ?? '',
            createdByName: auth.session.employee.Name,
            createdOn: moment.utc().valueOf(),
            updatedBy: auth.session.firebaseUser.uid,
            updatedByEmployee: auth.session.employee.EmployeeNumber ?? '',
            updatedByName: auth.session.employee.Name,
            updatedOn: moment.utc().valueOf(),
            paid: false,
            paidBy: null,
            paidByName: null,
            paidByEmployeeNumber: null,
            paidOn: null,
            journaled: false,
            journal: null,
            journalNr: null,
            journaledBy: null,
            journaledByName: null,
            journaledByEmployeeNumber: null,
            journaledOn: null,
            id: '',
            group: selectedGroup ?? '',
            monthDate: selectedAccountMonth.valueOf(),
            related: null,
            relatedId: null,
            reverted: false,
            credit: 0,
            isActive: true,
        });
    };
    
    const loadData = async () => {
        if (!selectedAccountMonth || !selectedGroup || !lpuTariffs.length) return;

        setIsLoading(true);

        const invoicesForSelectedMonth = (await TransformerInvoiceHelper.collection<ILPULine>()
            .where('type', '==', 'lpu')
            .where('monthDate', '==', firestore.Timestamp.fromMillis(selectedAccountMonth.valueOf()))
            .get())
            .docs
            .map(x => x.data())
            .filter(x => x.isActive);

        const newInvoices : Array<ITransformerInvoice<ILPULine>> = [];

        try {
            const startOfMonth = moment.utc(selectedAccountMonth.valueOf());
            const endOfMonth = moment.utc(selectedAccountMonth.valueOf()).endOf('month').startOf('day');

            transformers
                .forEach((transformer) => {
                    const lines : Array<ILPULine> = [];

                    const existingInvoice = invoicesForSelectedMonth.find(x => x.epNumber === transformer.EPNumber);

                    // Add invoice if it's already been captured for the current month.
                    if (existingInvoice) {
                        newInvoices.push({
                            ...existingInvoice,
                        });
                        return;
                    }

                    if (!transformer.TransmissionZone || !transformer.LineVoltage) return;

                    // When a tariff change occurs there are multiple tariffs that are used so then create one line per tariff.
                    if (lpuTariffs.length > 1) {
                        lpuTariffs.forEach((tariff, index) => {

                            const tariffStartDate = moment.utc(tariff.startDate).startOf('day');
                            const tariffEndDate = tariff.endDate 
                                ? moment.utc(tariff.endDate).startOf('day') 
                                : null;

                            const startDate = tariffStartDate.isAfter(startOfMonth) 
                                ? tariffStartDate 
                                : startOfMonth;

                            const endDate = tariffEndDate?.isBefore(endOfMonth) 
                                ? tariffEndDate 
                                : endOfMonth;

                            const tariffValues = TariffHelper.getLPUTariffValues(tariff, transformer, endDate);

                            const line : ILPULine = {
                                guid: uuid.v4(),
                                tariffValues,
                                tariff,
                                // TODO: Determine whether transmission zone, line voltage needs to be stored on line
                                // transmissionZone: transformer.TransmissionZone,
                                // lineVoltage: transformer.LineVoltage,
                                days: 0,
                                adminCharge: 0,
                                networkDemandUnits: 0,
                                ancillaryServiceCharge: 0,
                                unitsSTDLow: 0,
                                unitsOffPeakLow: 0,
                                unitsSTDHigh: 0,
                                unitsOffPeakHigh: 0,
                                unitsPeakHigh: 0,
                                unitsPeakLow: 0,
                                excessNetworkAccessCharge: 0,
                                excessReactiveEnergy: 0,
                                interestCharge: 0,
                                legacyCharge: 0,
                                total: 0,
                                isTotalsManuallyOverridden: false,
                            };

                            lines.push({ ...line });
                        });
                    } else {
                        const tariffValues = TariffHelper.getLPUTariffValues(lpuTariffs[0], transformer, endOfMonth);

                        const line : ILPULine = {
                            guid: uuid.v4(),
                            tariffValues,
                            days: selectedAccountMonth.daysInMonth(),
                            adminCharge: 0,
                            tariff: lpuTariffs[0],
                            networkDemandUnits: 0,
                            ancillaryServiceCharge: 0,
                            generationCapacityCharge: 0,
                            legacyCharge : 0,
                            unitsSTDLow: 0,
                            unitsOffPeakLow: 0,
                            unitsSTDHigh: 0,
                            unitsOffPeakHigh: 0,
                            unitsPeakHigh: 0,
                            unitsPeakLow: 0,
                            excessNetworkAccessCharge: 0,
                            excessReactiveEnergy: 0,
                            interestCharge: 0,
                            total: 0,
                            isTotalsManuallyOverridden: false,
                        };

                        lines.push({ ...line });
                    }

                    newInvoices.push(invoiceFromLines(transformer, lines));
                });
        } catch (ex) {
            generalFunctions.generalShowError(ex, `${ex}`);
        } finally {
            setIsLoading(false);
            setInvoices(newInvoices);
            setSelectedEpNumber(null);
            setCurrentInvoices(invoicesForSelectedMonth);
        }
    };

    const loadTariffs = async (selectedMonthDate : moment.Moment, currentTariffChange : boolean) => {
        setIsLoading(true);
        try {
            const tariffs = (await TariffHelper.lpuCollection().get())
                .docs
                .map(n => ({ ...n.data() }))
                .sort((a, b) => a.startDate - b.startDate);

            const newLpuTariffs = tariffs
                .filter(x => selectedMonthDate.isBetween(
                    moment.utc(x.startDate),
                    x.endDate ? moment.utc(x.endDate).subtract(1, 'day') : moment.utc(selectedMonthDate).endOf('month'),
                    'month', '[]'))
                .sort((a, b) => a.startDate - b.startDate);

            if (currentTariffChange && newLpuTariffs.length === 1) {
                const tariff = newLpuTariffs[0];
                const index = tariffs.findIndex(x => x.ref.id === tariff.ref.id);
                if (index > 0) {
                    newLpuTariffs.unshift(tariffs[index - 1]);
                }
            }
            setLpuTariffs(newLpuTariffs);
        } catch (ex) {
            generalFunctions.generalShowErrorSnackbar(`${ex}`);
        } finally {
            setIsLoading(false);
        }
    };
    
    useEffect(() => {
        if (selectedAccountMonth && selectedGroup && lpuTariffs.length && transformers.length) {
            loadData();
        }
    }, [selectedAccountMonth, selectedGroup, lpuTariffs, transformers]);

    const loadTransformers = async (selectedGroup : string) => {
        setIsLoading(true);

        try {
            const transformers = (await TransformerHelper.collection()
                .where('EPGroup', '==', selectedGroup)
                .get())
                .docs
                .map(x => x.data())
                .filter(x => !!x.IsActive);

            setTransformers(transformers);
        } catch (ex) {
            generalFunctions.generalShowError(ex, 'Error loading transformers.');
        } finally {
            setIsLoading(false);
        }
    };

    const onMonthDateChanged = (momentDateParam : moment.Moment | null) => {
        const newMonthDate = !momentDateParam ? null : moment.utc(momentDateParam.valueOf()).startOf('month');
        setLpuTariffs([]);
        setInvoices([]);
        setSelectedAccountMonth(newMonthDate);

        if (!newMonthDate) return;
        loadTariffs(newMonthDate, isTariffChange);
    };

    const onEPGroupChange = (value ?: ITransformerGroup) => {
        const newGroup = value?.name;
        setTransformers([]);
        setInvoices([]);
        setSelectedGroup(newGroup ?? null);

        if (!newGroup) return;
        loadTransformers(newGroup);
    };

    const onCaptureSubmit = (submittedInvoices : Array<ITransformerInvoice<ILPULine>>) => {
        setInvoices(submittedInvoices);
        setIsTariffConfirmationDialogOpen(true);
        setSelectedEpNumber(null);
    };

    const onConfirmCancel = () => {
        setIsTariffConfirmationDialogOpen(true);
    };

    const onSave = async () => {
        if (!auth.session) return;
        setIsLoading(true);

        try {
            const currentInvoicesNumbers = currentInvoices.map(x => x.epNumber);
            const invoicesToSave = invoices
                .filter(x => !currentInvoicesNumbers.includes(x.epNumber))
                .filter(x => x.totalBilled)
                .map(invoice => ({
                    ...invoice,
                    lines: [...invoice.lines],
                }));

            if (!invoicesToSave.length) {
                generalFunctions.generalShowSuccessSnackbar('No new invoices to save.');
                setIsLoading(false);

                props.onDismiss();
            }

            for (let index = 0; index < invoicesToSave.length; index++) {                
                const invoice = invoicesToSave[index];
                await TransformerInvoiceHelper.save(invoice);
            }
            
            generalFunctions.generalShowSuccessSnackbar('Success');
            props.onDismiss();
        } catch (ex) {
            generalFunctions.generalShowErrorSnackbarV2('Error occurred saving LPU invoices.', ex);
        } finally {
            setIsLoading(false);
        }
    };

    const onConfirmRowClick = (newSelectedEpNumber : string) => {
        setIsTariffConfirmationDialogOpen(true);
        setSelectedEpNumber(newSelectedEpNumber);
    };

    const onTariffChange = (event : React.ChangeEvent<HTMLInputElement>, checked : boolean) => {
        setIsTariffChange(checked);

        if (!selectedAccountMonth) return;

        loadTariffs(selectedAccountMonth, checked);
    };

    return (
        <Screen
            useAnimatedLoader
            isLoading={isLoading}>
            <div className='fdc flx1 p10 mh0 mw0 bcg0'>
                {
                    !isTariffConfirmationDialogOpen && (
                        <Toolbar>
                            <span className={'flx1'} />
                            <div className='fdr flx1 aifs jcfs'>
                                <div className={'flx1 aifs p5 mb10 pr20'}>
                                    <FormControl fullWidth>
                                        <DatePicker
                                            value={selectedAccountMonth}
                                            disabled={isLoading}
                                            views={['month']}
                                            maxDate={maxDate}
                                            onChange={onMonthDateChanged}
                                            format='MMM YYYY'
                                            label='Select Account Month...'
                                            id='monthDate'
                                            className={'TextField'}
                                            margin='normal'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <DropdownButton />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </div>
                                <div className={'flx1 aifs p5 mb10 pr20 mt16'}>
                                    <TransformerGroupDropdown
                                        fullWidth
                                        value={selectedGroup}
                                        onChange={onEPGroupChange}
                                        type={'LPU'}
                                        disabled={isLoading}
                                        required
                                    />
                                </div>
                                <div className={'fdc flx1 aifs p5 mb10 pr20 mt26'}>
                                    <div className='fdr aic'>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    value={isTariffChange}
                                                    onChange={onTariffChange}
                                                    color='primary'
                                                    checked={isTariffChange}
                                                />
                                            }
                                            labelPlacement='start'
                                            label='Tariff change?'
                                        />
                                    </div>
                                </div>
                            </div>
                        </Toolbar>
                    )
                }
                { !isTariffConfirmationDialogOpen && selectedAccountMonth && selectedGroup && (
                    <div className='fdc hfill mh0 mw0'>
                        <LPUCapturingTable
                            invoices={invoices}
                            disabledTransformers={currentInvoices.map(x => x.epNumber)}
                            multi={lpuTariffs.length > 1}
                            onSubmit={onCaptureSubmit}
                            selectedEpNumber={selectedEpNumber ?? undefined}
                        />
                    </div>
                )
                }
                { 
                    isTariffConfirmationDialogOpen &&
                    <div className='fdc flx1 mh0 mw0'>
                        <TariffConfirmTable
                            invoices={invoices}
                            group={selectedGroup ?? ''}
                            onCancelClick={onConfirmCancel}
                            onConfirmClick={onSave}
                            onRowClick={onConfirmRowClick}
                            disabledTransformers={currentInvoices.map(x => x.epNumber)}
                        />
                    </div>
                }
            </div>
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
        valueAddedTaxes: state.masterData.valueAddedTax.valueAddedTaxes,
    };
};

export default connect(mapStateToProps)(TransformerLPUCapture);
